import { call, put, takeEvery, all, select } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import * as authActionTypes from '../auth/actionTypes';
import axios from 'axios';
import { getEntityCall } from '../entities/sagas';
import { getRoleCall } from '../roles/sagas';
import { getUserPageRefresh } from '../auth/sagas';

const controllerUrl = '/users/';

const searchUsersCall = searchFields => new Promise((resolve, reject) => {
        axios.get(controllerUrl + 'search', {
            params: {
                ...searchFields,
                excludeSelf: false
            }
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
});

const getUserCall = (id) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + id).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const createUserCall = userData => new Promise((resolve, reject) => {
    axios.post(controllerUrl, userData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const editUserCall = (userId, userData) => new Promise((resolve, reject) => {
    axios.put(controllerUrl + userId, userData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const editLoggedInUserCall = (userData) => new Promise((resolve, reject) => {
    axios.put("/auth/personal", userData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const deleteUserCall = userId => new Promise((resolve, reject) => {
    axios.delete(controllerUrl + userId).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

function* searchUsersRequest({ searchFields }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const searchResults = yield call(() => searchUsersCall(searchFields));
        const entityIds = searchResults.content.map(user => user.entityId);
        const roleIds = searchResults.content.map(user => user.roleId);
        const uniqueEntityIds = [...new Set(entityIds)];
        const uniqueRoleIds = [...new Set(roleIds)];

        const entities = yield all(uniqueEntityIds.map(id => call(getEntityCall, id)));
        const roles = yield all(uniqueRoleIds.map(id => call(getRoleCall, id)));

        const searchResultsWithEntities = {
            ...searchResults,
            content: searchResults.content.map(
                user => ({
                    ...user,
                    entity: entities.find(e => e.id === user.entityId),
                    role: roles.find(r => r.id === user.roleId)
                })
            ),
            sort: searchResults.sort.map(sortCriteria => ({
                    ...sortCriteria,
                    property: fixSortProperty(sortCriteria)
                })
            )
        };

        yield put({
            type: actionTypes.USER_SEARCH_SUCCESS,
            payload: searchResultsWithEntities
        });
    } catch (e) {
        yield put({
            type: actionTypes.USER_SEARCH_FAILED,
            payload: e
        });
    }
}

function* getUserRequest({ userId, fetchRoleAndEntityInfo }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const user = yield call(getUserCall, userId);

        if(fetchRoleAndEntityInfo) {
            user.entity = yield call(getEntityCall, user.entityId);
            user.role = yield call(getRoleCall, user.roleId);
        } else {
            user.entity = user.entityId;
            user.role = user.roleId;
        }

        user.timezone = user.timeZone;

        yield put({
            type: actionTypes.GET_USER_SUCCESS,
            payload: user
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_USER_FAILED,
            payload: e
        });
    }
}

function* createUserRequest({ userData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        userData.roleId = userData.role;
        userData.entityId = userData.entity;
        userData.timeZone = userData.timezone;

        delete userData.role;
        delete userData.entity;
        delete userData.timezone;

        yield call(createUserCall, userData);

        yield put({
            type: actionTypes.CREATE_USER_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.CREATE_USER_FAILED
        })
    }
}

function* editUserRequest({ userId, userData, loggedInUser }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            console.log("editUser resetting user state");
            yield call(getUserPageRefresh);
        }
        else {console.log("edit userAlreadyDefined", userDefined);}
        userData.roleId = userData.role;
        userData.entityId = userData.entity;
        userData.timeZone = userData.timezone;

        delete userData.role;
        delete userData.entity;
        delete userData.timezone;

        if(!loggedInUser) {
            yield call(editUserCall, userId, userData);
        } else {
            yield call(editLoggedInUserCall, userData);
            yield put({
                type: authActionTypes.CHANGE_PERSONAL_INFO,
                payload: userData
            })
        }
        yield put({
            type: actionTypes.EDIT_USER_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.EDIT_USER_FAILED
        })
    }
}

function* deleteUserRequest({ userId }) {
    try {
        yield call(deleteUserCall, userId);

        yield put({
            type: actionTypes.DELETE_USER_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.DELETE_USER_FAILED
        })
    }
    return null
}

function fixSortProperty({property}) {
    switch (property) {
        case "role.name":
            return "role";
        default:
            return property
    }
}

const userSagas = [
    takeEvery(actionTypes.USER_SEARCH_REQUEST, searchUsersRequest),
    takeEvery(actionTypes.GET_USER_REQUEST, getUserRequest),
    takeEvery(actionTypes.CREATE_USER_REQUEST, createUserRequest),
    takeEvery(actionTypes.EDIT_USER_REQUEST, editUserRequest),
    takeEvery(actionTypes.DELETE_USER_REQUEST, deleteUserRequest),
];

export {
    userSagas,
    getUserCall
};